import type { Selection } from '../domain/types';

export type SelectionEdge = 'start' | 'end';

// How many px from a row/column edge count as "on the strip". Matches SCSS $selection-edge-strip-size.
const SELECTION_EDGE_STRIP_PX = 8;
const SELECTION_EDGE_STRIP_MIN_PX = 2;
const SELECTION_EDGE_STRIP_FULL_ROW_HEIGHT_PX = 32;
const GROUPED_ROW_SEAM_BLEED_PX = 4;
const COLUMN_DIVIDER_HIT_PX = 12;
const GAP_COLUMN_EDGE_BLEED_DIVISOR = 4;

// Short rows get a narrower strip so top/bottom strips don't overlap in the middle.
export function resolveRowSelectionEdgeStripPx(rowHeight: number): number {
  if (!Number.isFinite(rowHeight) || rowHeight <= 0) {
    return SELECTION_EDGE_STRIP_PX;
  }
  if (rowHeight >= SELECTION_EDGE_STRIP_FULL_ROW_HEIGHT_PX) {
    return SELECTION_EDGE_STRIP_PX;
  }

  const minCenterPx = 2;
  const maxStripWithoutOverlap = Math.floor((rowHeight - minCenterPx) / 2);
  if (maxStripWithoutOverlap <= 0) {
    return 1;
  }

  const proportional = Math.max(
    SELECTION_EDGE_STRIP_MIN_PX,
    Math.floor(rowHeight / 4),
  );
  return Math.max(
    1,
    Math.min(SELECTION_EDGE_STRIP_PX, proportional, maxStripWithoutOverlap),
  );
}

// Pairs with --grouped-row-seam-bleed in emailBuilderSelectionEdgeStrips.scss.
export function resolveGroupedRowSeamBleedPx(rowHeight: number): number {
  if (
    !Number.isFinite(rowHeight) ||
    rowHeight < SELECTION_EDGE_STRIP_FULL_ROW_HEIGHT_PX
  ) {
    return 0;
  }
  return GROUPED_ROW_SEAM_BLEED_PX;
}

function readRowSelectionEdgeStripPx(rowFrame: HTMLElement | null): number {
  if (
    rowFrame === null ||
    typeof rowFrame.getBoundingClientRect !== 'function'
  ) {
    return SELECTION_EDGE_STRIP_PX;
  }
  return resolveRowSelectionEdgeStripPx(
    rowFrame.getBoundingClientRect().height,
  );
}

function columnGapEdgeBleedPx(gapWidth: number): number {
  if (gapWidth <= 0) {
    return 0;
  }
  return Math.min(
    SELECTION_EDGE_STRIP_PX,
    Math.max(1, Math.floor(gapWidth / GAP_COLUMN_EDGE_BLEED_DIVISOR)),
  );
}

function isPointerInColumnFrameEdgeStripZone(args: {
  clientX: number;
  clientY: number;
  columnFrame: HTMLElement;
}): boolean {
  const rect = args.columnFrame.getBoundingClientRect();
  if (args.clientY < rect.top || args.clientY > rect.bottom) {
    return false;
  }
  const distStart = args.clientX - rect.left;
  const distEnd = rect.right - args.clientX;
  return (
    (distStart >= 0 && distStart <= SELECTION_EDGE_STRIP_PX) ||
    (distEnd >= 0 && distEnd <= SELECTION_EDGE_STRIP_PX)
  );
}

function isPointerInRowFrameEdgeStripZone(args: {
  clientX: number;
  clientY: number;
  rowFrame: HTMLElement;
}): boolean {
  const rect = args.rowFrame.getBoundingClientRect();
  if (args.clientX < rect.left || args.clientX > rect.right) {
    return false;
  }
  const stripPx = readRowSelectionEdgeStripPx(args.rowFrame);
  const distStart = args.clientY - rect.top;
  const distEnd = rect.bottom - args.clientY;
  return (
    (distStart >= 0 && distStart <= stripPx) ||
    (distEnd >= 0 && distEnd <= stripPx)
  );
}

export function resolveRowFrameFromHoverEvent(
  event: Pick<MouseEvent, 'currentTarget'>,
): HTMLElement | null {
  const target = event.currentTarget;
  if (!(target instanceof HTMLElement)) {
    return null;
  }
  const frame = target.closest('.email-builder-canvas-frame[data-level="row"]');
  return frame instanceof HTMLElement ? frame : target;
}

export function isPointerOnFrameLabel(args: {
  clientX: number;
  clientY: number;
}): boolean {
  if (typeof document === 'undefined') {
    return false;
  }
  if (!Number.isFinite(args.clientX) || !Number.isFinite(args.clientY)) {
    return false;
  }
  return document
    .elementsFromPoint(args.clientX, args.clientY)
    .some(
      element =>
        element.closest('[data-frame-label]') !== null ||
        element.closest('.email-builder-canvas-frame__label-hit') !== null,
    );
}

export function resolveColumnFrameFromHoverEvent(
  event: Pick<MouseEvent, 'currentTarget'>,
): HTMLElement | null {
  const target = event.currentTarget;
  if (!(target instanceof HTMLElement)) {
    return null;
  }
  const frame = target.closest(
    '.email-builder-canvas-frame[data-level="column"]',
  );
  return frame instanceof HTMLElement ? frame : target;
}

function readColumnElements(rowFrame: HTMLElement): HTMLElement[] {
  return Array.from(
    rowFrame.querySelectorAll('.email-builder-canvas-frame[data-level="column"]'),
  ).filter(
    (column): column is HTMLElement =>
      typeof HTMLElement === 'undefined' || column instanceof HTMLElement,
  );
}

export function isPointerInColumnGap(args: {
  clientX: number;
  clientY: number;
  rowFrame: HTMLElement | null;
}): boolean {
  if (args.rowFrame === null) {
    return false;
  }

  const columns = readColumnElements(args.rowFrame);
  for (let index = 0; index < columns.length - 1; index += 1) {
    const leftColumn = columns[index];
    const rightColumn = columns[index + 1];
    const leftRect = leftColumn.getBoundingClientRect();
    const rightRect = rightColumn.getBoundingClientRect();
    if (rightRect.left <= leftRect.right) {
      continue;
    }
    if (
      args.clientX >= leftRect.right &&
      args.clientX <= rightRect.left &&
      args.clientY >= leftRect.top &&
      args.clientY <= leftRect.bottom
    ) {
      return true;
    }
  }

  return false;
}

function isPointerOverColumnInRow(args: {
  clientX: number;
  clientY: number;
  rowFrame: HTMLElement | null;
}): boolean {
  if (args.rowFrame === null) {
    return false;
  }

  for (const column of readColumnElements(args.rowFrame)) {
    const rect = column.getBoundingClientRect();
    if (
      args.clientX >= rect.left &&
      args.clientX <= rect.right &&
      args.clientY >= rect.top &&
      args.clientY <= rect.bottom
    ) {
      return true;
    }
  }

  return false;
}

function isPointerOnChromeSelector(
  args: { clientX: number; clientY: number },
  selector: string,
): boolean {
  if (typeof document === 'undefined') {
    return false;
  }
  if (!Number.isFinite(args.clientX) || !Number.isFinite(args.clientY)) {
    return false;
  }
  const top = document.elementFromPoint(args.clientX, args.clientY);
  return top instanceof Element && top.closest(selector) !== null;
}

export function isPointerOnColumnResizeHandle(args: {
  clientX: number;
  clientY: number;
}): boolean {
  return isPointerOnChromeSelector(args, '.email-builder-column-resize-handle');
}

export function isPointerOnBlockResizeHandle(args: {
  clientX: number;
  clientY: number;
}): boolean {
  return isPointerOnChromeSelector(args, '.email-builder-image-resize-handle');
}

function readColumnId(column: HTMLElement): string | null {
  const entityId = column.getAttribute('data-entity-id');
  if (entityId === null) {
    return null;
  }
  const parts = entityId.split(':');
  return parts.length === 3 ? parts[2] : null;
}

export type ColumnContainerEdgeAtSeam = {
  columnId: string;
  edge: SelectionEdge;
};

export type ColumnSeamEdgeHighlight = {
  rowId: string;
  columnId: string;
  edge: SelectionEdge;
};

export type RowEdgeHighlight = {
  rowId: string;
  edge: SelectionEdge;
};

export type SelectionEdgePointerTarget =
  | RowEdgeHighlight
  | ColumnSeamEdgeHighlight;

function readRowIdFromFrame(rowFrame: HTMLElement): string | null {
  const entityId = rowFrame.getAttribute('data-entity-id');
  if (entityId === null || !entityId.startsWith('row:')) {
    return null;
  }
  return entityId.slice('row:'.length);
}

function findRowFrameAtPointer(
  clientX: number,
  clientY: number,
): HTMLElement | null {
  if (typeof document === 'undefined') {
    return null;
  }
  for (const element of document.elementsFromPoint(clientX, clientY)) {
    if (!(element instanceof Element)) {
      continue;
    }
    const frame = element.closest('.email-builder-canvas-frame[data-level="row"]');
    if (frame instanceof HTMLElement) {
      return frame;
    }
  }
  return null;
}

function findColumnFrameAtPointer(
  clientX: number,
  clientY: number,
): HTMLElement | null {
  if (typeof document === 'undefined') {
    return null;
  }
  for (const element of document.elementsFromPoint(clientX, clientY)) {
    if (!(element instanceof Element)) {
      continue;
    }
    const frame = element.closest(
      '.email-builder-canvas-frame[data-level="column"]',
    );
    if (frame instanceof HTMLElement) {
      return frame;
    }
  }
  return null;
}

export function resolveRowEdgeStripAtPointer(args: {
  clientX: number;
  clientY: number;
  rowFrame: HTMLElement;
}): SelectionEdge | null {
  if (isPointerOnFrameLabel(args)) {
    return null;
  }
  const rect = args.rowFrame.getBoundingClientRect();
  if (args.clientX < rect.left || args.clientX > rect.right) {
    return null;
  }
  const stripPx = readRowSelectionEdgeStripPx(args.rowFrame);
  const distStart = args.clientY - rect.top;
  const distEnd = rect.bottom - args.clientY;
  const nearStart = distStart >= 0 && distStart <= stripPx;
  const nearEnd = distEnd >= 0 && distEnd <= stripPx;
  if (!nearStart && !nearEnd) {
    return null;
  }
  if (nearStart && nearEnd) {
    return distStart <= distEnd ? 'start' : 'end';
  }
  return nearStart ? 'start' : 'end';
}

export function resolveSelectionEdgePointerTarget(args: {
  clientX: number;
  clientY: number;
  selection: Selection;
  textEditingBlock: { rowId: string; columnId: string } | null;
}): SelectionEdgePointerTarget | null {
  if (
    !Number.isFinite(args.clientX) ||
    !Number.isFinite(args.clientY) ||
    isPointerOnFrameLabel(args) ||
    isPointerOnColumnResizeHandle(args) ||
    isPointerOnBlockResizeHandle(args)
  ) {
    return null;
  }

  const rowFrame = findRowFrameAtPointer(args.clientX, args.clientY);
  if (rowFrame !== null) {
    const rowEdge = resolveRowEdgeStripAtPointer({
      clientX: args.clientX,
      clientY: args.clientY,
      rowFrame,
    });
    const rowId = readRowIdFromFrame(rowFrame);
    if (rowEdge !== null && rowId !== null) {
      return { rowId, edge: rowEdge };
    }
  }

  const columnFrame = findColumnFrameAtPointer(args.clientX, args.clientY);
  if (columnFrame === null || rowFrame === null) {
    return null;
  }

  const columnId = readColumnId(columnFrame);
  const rowId = readRowIdFromFrame(rowFrame);
  if (columnId === null || rowId === null) {
    return null;
  }

  const blockActiveInColumn =
    (args.selection.kind === 'block' &&
      args.selection.rowId === rowId &&
      args.selection.columnId === columnId) ||
    (args.textEditingBlock !== null &&
      args.textEditingBlock.rowId === rowId &&
      args.textEditingBlock.columnId === columnId);

  const edge = resolveColumnEdgeStripHighlightForColumn({
    clientX: args.clientX,
    clientY: args.clientY,
    columnId,
    columnFrame,
    rowFrame,
    blockActiveInColumn,
  });
  if (edge === null) {
    return null;
  }

  return { rowId, columnId, edge };
}

// Sets data-forced-highlight on edge strips (see SelectionEdgeStrips.tsx).
export function syncCanvasSelectionEdgeChrome(args: {
  clientX: number;
  clientY: number;
  selection: Selection;
  textEditingBlock: { rowId: string; columnId: string } | null;
}): {
  rowEdgeHighlight: RowEdgeHighlight | null;
  columnSeamEdgeHighlight: ColumnSeamEdgeHighlight | null;
} {
  if (!Number.isFinite(args.clientX) || !Number.isFinite(args.clientY)) {
    return { rowEdgeHighlight: null, columnSeamEdgeHighlight: null };
  }

  if (isPointerOnFrameLabel(args)) {
    return { rowEdgeHighlight: null, columnSeamEdgeHighlight: null };
  }

  let rowEdgeHighlight: RowEdgeHighlight | null = null;
  const rowFrame = findRowFrameAtPointer(args.clientX, args.clientY);
  if (rowFrame !== null) {
    const edge = resolveRowEdgeStripAtPointer({
      clientX: args.clientX,
      clientY: args.clientY,
      rowFrame,
    });
    const rowId = readRowIdFromFrame(rowFrame);
    if (edge !== null && rowId !== null) {
      rowEdgeHighlight = { rowId, edge };
    }
  }

  let columnSeamEdgeHighlight: ColumnSeamEdgeHighlight | null = null;
  const columnFrame = findColumnFrameAtPointer(args.clientX, args.clientY);
  if (columnFrame !== null && rowFrame !== null) {
    const columnId = readColumnId(columnFrame);
    const rowId = readRowIdFromFrame(rowFrame);
    if (columnId !== null && rowId !== null) {
      const columnDirectlySelected =
        args.selection.kind === 'column' &&
        args.selection.rowId === rowId &&
        args.selection.columnId === columnId;
      const blockActiveInColumn =
        (args.selection.kind === 'block' &&
          args.selection.rowId === rowId &&
          args.selection.columnId === columnId) ||
        (args.textEditingBlock !== null &&
          args.textEditingBlock.rowId === rowId &&
          args.textEditingBlock.columnId === columnId);
      if (!columnDirectlySelected && !blockActiveInColumn) {
        const edge = resolveColumnEdgeStripHighlightForColumn({
          clientX: args.clientX,
          clientY: args.clientY,
          columnId,
          columnFrame,
          rowFrame,
          blockActiveInColumn,
        });
        if (edge !== null) {
          columnSeamEdgeHighlight = { rowId, columnId, edge };
        }
      }
    }
  }

  return { rowEdgeHighlight, columnSeamEdgeHighlight };
}

// Pointer in the gap between columns can target either column's edge.
export function resolveColumnContainerEdgeAtSeam(args: {
  clientX: number;
  clientY: number;
  rowFrame: HTMLElement | null;
}): ColumnContainerEdgeAtSeam | null {
  if (args.rowFrame === null) {
    return null;
  }

  const columns = readColumnElements(args.rowFrame);
  if (columns.length < 2) {
    return null;
  }

  for (let index = 0; index < columns.length - 1; index += 1) {
    const leftColumn = columns[index];
    const rightColumn = columns[index + 1];
    const leftRect = leftColumn.getBoundingClientRect();
    const rightRect = rightColumn.getBoundingClientRect();
    if (
      args.clientY < leftRect.top ||
      args.clientY > leftRect.bottom
    ) {
      continue;
    }

    const seamStart = leftRect.right;
    const seamEnd = rightRect.left;
    const hasGap = seamEnd > seamStart;
    const gapBleed = hasGap ? columnGapEdgeBleedPx(seamEnd - seamStart) : 0;

    const distFromLeftSeam = args.clientX - seamStart;
    const distFromRightSeam = seamEnd - args.clientX;
    const inLeftStrip =
      (distFromLeftSeam <= 0 &&
        -distFromLeftSeam <= SELECTION_EDGE_STRIP_PX) ||
      (hasGap &&
        distFromLeftSeam > 0 &&
        distFromLeftSeam <= gapBleed);
    const inRightStrip =
      (distFromRightSeam <= 0 &&
        -distFromRightSeam <= SELECTION_EDGE_STRIP_PX) ||
      (hasGap &&
        distFromRightSeam > 0 &&
        distFromRightSeam <= gapBleed);

    if (!inLeftStrip && !inRightStrip) {
      continue;
    }

    if (inLeftStrip && inRightStrip) {
      const leftColumnId = readColumnId(leftColumn);
      const rightColumnId = readColumnId(rightColumn);
      if (leftColumnId === null || rightColumnId === null) {
        return null;
      }
      const leftDistance =
        distFromLeftSeam <= 0 ? -distFromLeftSeam : distFromLeftSeam;
      const rightDistance =
        distFromRightSeam <= 0 ? -distFromRightSeam : distFromRightSeam;
      return leftDistance <= rightDistance
        ? { columnId: leftColumnId, edge: 'end' }
        : { columnId: rightColumnId, edge: 'start' };
    }

    if (inLeftStrip) {
      const leftColumnId = readColumnId(leftColumn);
      return leftColumnId === null ? null : { columnId: leftColumnId, edge: 'end' };
    }

    const rightColumnId = readColumnId(rightColumn);
    return rightColumnId === null
      ? null
      : { columnId: rightColumnId, edge: 'start' };
  }

  return null;
}

export function resolveColumnEdgeStripHighlightForColumn(args: {
  clientX: number;
  clientY: number;
  columnId: string;
  columnFrame: HTMLElement | null;
  rowFrame: HTMLElement | null;
  blockActiveInColumn?: boolean;
}): SelectionEdge | null {
  if (args.blockActiveInColumn) {
    return null;
  }

  const seam = resolveColumnContainerEdgeAtSeam({
    clientX: args.clientX,
    clientY: args.clientY,
    rowFrame: args.rowFrame,
  });

  let edge: SelectionEdge | null = null;
  if (seam !== null && seam.columnId === args.columnId) {
    edge = seam.edge;
  } else if (args.columnFrame !== null) {
    const rect = args.columnFrame.getBoundingClientRect();
    if (
      args.clientY >= rect.top &&
      args.clientY <= rect.bottom
    ) {
      const distStart = args.clientX - rect.left;
      const distEnd = rect.right - args.clientX;
      const nearStart =
        distStart >= 0 && distStart <= SELECTION_EDGE_STRIP_PX;
      const nearEnd = distEnd >= 0 && distEnd <= SELECTION_EDGE_STRIP_PX;
      if (nearStart && nearEnd) {
        edge = distStart <= distEnd ? 'start' : 'end';
      } else if (nearStart) {
        edge = 'start';
      } else if (nearEnd) {
        edge = 'end';
      }
    }
  }

  return edge;
}

export function shouldPromoteColumnDividerRowHover(args: {
  clientX: number;
  clientY: number;
  rowFrame?: HTMLElement | null;
}): boolean {
  const rowFrame = args.rowFrame ?? null;
  if (
    !isPointerInColumnGap({
      clientX: args.clientX,
      clientY: args.clientY,
      rowFrame,
    })
  ) {
    return false;
  }
  return resolveColumnContainerEdgeAtSeam({
    clientX: args.clientX,
    clientY: args.clientY,
    rowFrame,
  }) === null;
}

export function emailBuilderColumnSelectionSuppressedEdges(
  blockActiveInColumn = false,
): SelectionEdge[] {
  return blockActiveInColumn ? ['start', 'end'] : [];
}

// Row hover is deferred when the pointer is over column content or near a resize handle
// so column/block interactions win over parent row hover.
export function resolveRowHoverDeferReason(args: {
  clientX: number;
  clientY: number;
  rowFrame: HTMLElement | null;
}): 'column-content' | 'column-divider-band' | null {
  if (
    args.rowFrame !== null &&
    isPointerInRowFrameEdgeStripZone({
      clientX: args.clientX,
      clientY: args.clientY,
      rowFrame: args.rowFrame,
    })
  ) {
    return null;
  }

  if (resolveColumnContainerEdgeAtSeam(args) !== null) {
    return 'column-content';
  }

  if (shouldPromoteColumnDividerRowHover(args)) {
    return null;
  }

  if (
    isPointerOnFrameLabel(args) ||
    isPointerOnBlockResizeHandle(args) ||
    isPointerOnColumnResizeHandle(args) ||
    isPointerOverColumnInRow(args)
  ) {
    return 'column-content';
  }

  if (args.rowFrame === null) {
    return null;
  }

  const columns = readColumnElements(args.rowFrame);
  if (columns.length < 2) {
    return null;
  }

  for (let index = 0; index < columns.length; index += 1) {
    const column = columns[index];
    const rect = column.getBoundingClientRect();
    const distStart = args.clientX - rect.left;
    const distEnd = rect.right - args.clientX;
    if (
      index > 0 &&
      distStart > SELECTION_EDGE_STRIP_PX &&
      distStart <= COLUMN_DIVIDER_HIT_PX
    ) {
      return 'column-divider-band';
    }
    if (
      index < columns.length - 1 &&
      distEnd > SELECTION_EDGE_STRIP_PX &&
      distEnd <= COLUMN_DIVIDER_HIT_PX
    ) {
      return 'column-divider-band';
    }
  }

  return null;
}

// Same idea for column hover. Resize handles and block content take priority.
export function resolveColumnHoverDeferReason(args: {
  rowId: string;
  columnId: string;
  columnIndex: number;
  columnCount: number;
  selection: Selection;
  textEditingBlock: { rowId: string; columnId: string } | null;
  clientX: number;
  clientY: number;
  columnFrame: HTMLElement | null;
}): 'column-resize-handle' | 'divider-edge' | 'block-active-edge' | 'block-content' | null {
  const { columnIndex, columnCount } = args;

  if (
    isBlockActiveInColumn(args) &&
    typeof document !== 'undefined' &&
    Number.isFinite(args.clientX) &&
    Number.isFinite(args.clientY) &&
    isPointerOnBlockResizeHandle(args)
  ) {
    return 'block-content';
  }

  if (
    args.columnFrame !== null &&
    isBlockActiveInColumn(args) &&
    isPointerInColumnFrameEdgeStripZone({
      clientX: args.clientX,
      clientY: args.clientY,
      columnFrame: args.columnFrame,
    })
  ) {
    return 'block-active-edge';
  }

  if (
    args.columnFrame !== null &&
    isPointerInColumnFrameEdgeStripZone({
      clientX: args.clientX,
      clientY: args.clientY,
      columnFrame: args.columnFrame,
    })
  ) {
    return null;
  }

  if (
    typeof document !== 'undefined' &&
    Number.isFinite(args.clientX) &&
    Number.isFinite(args.clientY)
  ) {
    if (isPointerOnFrameLabel(args)) {
      return 'block-content';
    }
    const top = document.elementFromPoint(args.clientX, args.clientY);
    if (top instanceof Element) {
      if (isPointerOnColumnResizeHandle(args)) {
        return 'divider-edge';
      }
      if (isPointerOverBlockInColumn(args, top)) {
        return 'block-content';
      }
    }
  }

  if (args.columnFrame === null) {
    return null;
  }

  const rect = args.columnFrame.getBoundingClientRect();
  const distStart = args.clientX - rect.left;
  const distEnd = rect.right - args.clientX;

  if (columnCount >= 2) {
    if (
      columnIndex > 0 &&
      distStart > SELECTION_EDGE_STRIP_PX &&
      distStart <= COLUMN_DIVIDER_HIT_PX
    ) {
      return 'divider-edge';
    }
    if (
      columnIndex < columnCount - 1 &&
      distEnd > SELECTION_EDGE_STRIP_PX &&
      distEnd <= COLUMN_DIVIDER_HIT_PX
    ) {
      return 'divider-edge';
    }
  }

  return null;
}

function isPointerOverBlockInColumn(
  args: {
    rowId: string;
    columnId: string;
  },
  top: Element,
): boolean {
  const blockFrame = top.closest(
    '.email-builder-canvas-frame[data-level="block"]',
  );
  if (!(blockFrame instanceof Element)) {
    return false;
  }
  const columnFrame = blockFrame.closest(
    '.email-builder-canvas-frame[data-level="column"]',
  );
  if (!(columnFrame instanceof Element)) {
    return false;
  }
  return (
    columnFrame.getAttribute('data-entity-id') ===
    `column:${args.rowId}:${args.columnId}`
  );
}

function isBlockActiveInColumn(args: {
  rowId: string;
  columnId: string;
  selection: Selection;
  textEditingBlock: { rowId: string; columnId: string } | null;
}): boolean {
  if (
    args.selection.kind === 'block' &&
    args.selection.rowId === args.rowId &&
    args.selection.columnId === args.columnId
  ) {
    return true;
  }
  return (
    args.textEditingBlock !== null &&
    args.textEditingBlock.rowId === args.rowId &&
    args.textEditingBlock.columnId === args.columnId
  );
}
